import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useRef } from "react";
import GetGatheringAndUsersDocument from "src/api/graphql/__generated__/documents/GetGatheringAndUsersDocument";
import log from "src/helpers/log";
import useStateRef from "src/hooks/useStateRef";
import toast from "src/swsh-native/toast";

const BASE_FETCH_AMOUNT = 32;
const useGatheringAndUsers = (gatheringId: string, { search }: { search?: string } = {}) => {
	const [loading, setLoading, loadingRef] = useStateRef(false);
	const [refreshing, setRefreshing, refreshingRef] = useStateRef(false);
	const { data, previousData, fetchMore, refetch } = useQuery(GetGatheringAndUsersDocument, {
		fetchPolicy: "cache-first",
		variables: {
			input: {
				gatheringId,
			},
			usersInput: {
				limit: BASE_FETCH_AMOUNT,
				search,
			},
		},
		// If you have pagination issues here, try including `returnPartialData`
		// We've had bugs before in which a missing field invalidated the incoming results
	});
	const cursor = useRef<string | null>(null);
	useEffect(() => {
		if (data?.getGathering?.users?.cursor) {
			cursor.current = data.getGathering.users.cursor;
		}
	}, [data?.getGathering?.users?.cursor]);

	const handleFetchMore = useCallback(async () => {
		if (loadingRef.current) return;
		if (refreshingRef.current) return;
		if (cursor.current === "{}") return;
		if (search) return;
		setLoading(true);
		try {
			const { data } = await fetchMore({
				variables: {
					input: {
						gatheringId,
					},
					usersInput: {
						limit: BASE_FETCH_AMOUNT,
						cursor: cursor.current,
					},
				},
			});
			cursor.current = data?.getGathering?.users?.cursor ?? null;
		} catch (err) {
			log.error({ err }, "Error fetching more users");
			toast({
				preset: "error",
				title: "Error getting viewers",
			});
		} finally {
			setLoading(false);
		}
	}, [fetchMore, gatheringId, loadingRef, refreshingRef, search, setLoading]);
	const handleRefresh = useCallback(async () => {
		try {
			setRefreshing(true);
			const { data } = await refetch({
				input: {
					gatheringId,
				},
				usersInput: {
					limit: BASE_FETCH_AMOUNT,
					search,
				},
			});
			cursor.current = data?.getGathering?.users?.cursor ?? null;
		} catch (err) {
			log.error({ err }, "Error refreshing gathering and users");
			toast({
				preset: "error",
				title: "Error refreshing",
			});
		} finally {
			setRefreshing(false);
		}
	}, [setRefreshing, refetch, gatheringId, search]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: Only run on mount
	useEffect(() => {
		handleRefresh();
	}, []);

	return {
		gathering: (data ?? previousData)?.getGathering,
		loading,
		refreshing,
		fetchMore: handleFetchMore,
		refresh: handleRefresh,
	};
};

export type UseGatheringAndUsers_Gathering = NonNullable<
	ReturnType<typeof useGatheringAndUsers>["gathering"]
>;
export type UseGatheringAndUsers_User = NonNullable<
	NonNullable<
		NonNullable<ReturnType<typeof useGatheringAndUsers>["gathering"]>["users"]
	>["payload"]
>[number];

export default useGatheringAndUsers;
